import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import {
  Bold,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo,
  RemoveFormatting,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InsertButtonIcon from '@/assets/icons/insert-button.svg';
import {
  type ActionButtonAttributes,
  type ActionButtonUpdateAttributes,
  ActionButton,
} from '@/components/ui/tiptap-editor/extensions/action-button';
import { getActionButtonsFromEditor } from '@/components/ui/tiptap-editor/utils/get-button-actions-from-editor';
import { getSuggestionOptions } from '@/components/ui/tiptap-editor/utils/suggestion';

import { Button } from '../button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import { Input } from '../input';
import {
  type ActionButtonEditDialogValues,
  ActionButtonEditDialog,
} from './components/action-button-edit-dialog';
import { WorkerAttributeExtension } from './extensions/worker-attribute';

import type { WorkerAttr } from '@/types/worker-attr';

function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export type TiptapFeature =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'bulletList'
  | 'orderedList'
  | 'blockquote'
  | 'link'
  | 'image'
  | 'imageUrl'
  | 'mergeKeys'
  | 'actionButton'
  | 'undo'
  | 'redo'
  | 'clearFormatting';

const FULL_FEATURES: TiptapFeature[] = [
  'bold',
  'italic',
  'underline',
  'bulletList',
  'orderedList',
  'blockquote',
  'link',
  'image',
  'imageUrl',
  'mergeKeys',
  'actionButton',
  'undo',
  'redo',
];

const BASIC_FEATURES: TiptapFeature[] = [
  'bold',
  'italic',
  'underline',
  'bulletList',
  'orderedList',
  'blockquote',
  'link',
  'image',
  'imageUrl',
  'undo',
  'redo',
];

interface TiptapEditorProps {
  content: string;
  onChange?: (html: string) => void;
  /**
   * Called on every editor update with the current list of Action Buttons in the document.
   * Use this to keep an external `buttons[]` array in sync.
   */
  onActionButtonsChange?: (buttons: ActionButtonAttributes[]) => void;
  /**
   * Called when an Action Button node is clicked (NodeView).
   * `TiptapEditor` also includes a built-in edit dialog for label/color/link.
   */
  onActionButtonClick?: (attrs: ActionButtonAttributes) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  /** Controls visual styling - borders, background, toolbar appearance */
  appearance?: 'bordered' | 'borderless';
  /** Controls which toolbar features are shown */
  preset?: 'full' | 'basic';
  /** Override the preset with custom features */
  features?: TiptapFeature[];
  /** Worker attributes for mention/autocomplete functionality */
  workerAttributes?: WorkerAttr[];
  /** Controls the height of the editor */
  height?: 'fixed' | 'full';
  /**
   * Defaults applied when inserting a new Action Button, and as fallbacks when
   * editing an existing button that is missing attributes.
   */
  actionButtonDefaults?: Partial<ActionButtonEditDialogValues>;
}

export function TiptapEditor({
  content,
  onChange,
  onActionButtonsChange,
  onActionButtonClick,
  placeholder = 'Start typing...',
  editable = true,
  className,
  appearance = 'bordered',
  preset = 'full',
  features: featuresProp,
  workerAttributes = [],
  height = 'fixed',
  actionButtonDefaults,
}: TiptapEditorProps) {
  const intl = useIntl();
  const [, setEditorState] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const resolvedActionButtonDefaults = useMemo<ActionButtonEditDialogValues>(
    () => ({
      label:
        actionButtonDefaults?.label ??
        intl.formatMessage({
          defaultMessage: 'Button',
          description:
            'Default label for call-to-action block in rich text editor',
        }),
      link: actionButtonDefaults?.link ?? '',
      backgroundColor: actionButtonDefaults?.backgroundColor ?? '#2600FF',
    }),
    [actionButtonDefaults, intl],
  );
  const actionButtonDefaultsRef = useLatest(resolvedActionButtonDefaults);

  // Determine which features to show
  const features =
    featuresProp ?? (preset === 'basic' ? BASIC_FEATURES : FULL_FEATURES);
  const hasFeature = (feature: TiptapFeature) => features.includes(feature);
  const isBorderless = appearance === 'borderless';
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrlDialogOpen, setImageUrlDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Action Button edit dialog state
  const [actionButtonDialogValues, setActionButtonDialogValues] =
    useState<ActionButtonEditDialogValues | null>(null);
  const actionButtonUpdateRef = useRef<ActionButtonUpdateAttributes | null>(
    null,
  );

  // Create suggestion options if worker attributes are provided
  // Cast to the type Mention expects - our implementation is compatible
  const suggestion =
    workerAttributes.length > 0
      ? (getSuggestionOptions(workerAttributes) as unknown as Omit<
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          import('@tiptap/suggestion').SuggestionOptions<any, any>,
          'editor'
        >)
      : undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extensions: any[] = [
    StarterKit.configure({
      // Exclude Link and Underline from StarterKit since we configure them separately
      link: false,
      underline: false,
    }),
    Underline,
    Image.configure({
      inline: true,
      allowBase64: true,
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded',
      },
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'cursor-pointer text-blue-600 underline',
      },
    }),
    // Action button node (block) + NodeView renderer
    ActionButton.configure({
      onClick: (attrs, helpers) => {
        // Allow consumers to react to button clicks, but handle editing here.
        onActionButtonClick?.(attrs);

        if (!editable) {
          return;
        }

        const defaults = actionButtonDefaultsRef.current;

        setActionButtonDialogValues({
          label:
            typeof attrs.value === 'string' && attrs.value.trim().length > 0
              ? attrs.value
              : defaults.label,
          link:
            typeof attrs.link === 'string' && attrs.link.trim().length > 0
              ? attrs.link
              : defaults.link,
          backgroundColor: attrs.backgroundColor ?? defaults.backgroundColor,
        });
        actionButtonUpdateRef.current = helpers.updateAttributes;
      },
    }),
    Placeholder.configure({
      placeholder,
    }),
  ];

  // Add worker attribute extension and mention if attributes are provided
  // IMPORTANT: WorkerAttributeExtension must be added BEFORE Mention extension
  if (workerAttributes.length > 0) {
    extensions.push(WorkerAttributeExtension);
    if (suggestion) {
      extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class:
              'mx-[3px] rounded-md px-[4px] py-[1px] bg-blue-50 text-blue-700 text-sm',
          },
          suggestion,
        }),
      );
    }
  }

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
      onActionButtonsChange?.(getActionButtonsFromEditor(editor));
      // Force re-render to update button states
      setEditorState(prev => prev + 1);
    },
    onSelectionUpdate: () => {
      // Force re-render when selection changes to update active button states
      setEditorState(prev => prev + 1);
    },
    editorProps: {
      attributes: {
        class: clsx(
          'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
          className,
        ),
      },
      handleDrop: (view, event, _slice, moved) => {
        setIsDragging(false);

        if (!moved && event.dataTransfer?.files?.length) {
          const file = event.dataTransfer.files[0];

          // Check if the dropped file is an image
          if (file.type.startsWith('image/')) {
            event.preventDefault();

            const reader = new FileReader();
            reader.onload = e => {
              const src = e.target?.result as string;
              const { schema } = view.state;
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });

              if (coordinates) {
                const node = schema.nodes.image?.create({ src });
                if (node) {
                  const transaction = view.state.tr.insert(
                    coordinates.pos,
                    node,
                  );
                  view.dispatch(transaction);
                }
              }
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
      handleDOMEvents: {
        dragover: (view, event) => {
          const hasFiles = event.dataTransfer?.types?.includes('Files');
          if (hasFiles) {
            event.preventDefault();
            setIsDragging(true);
          }
          return false;
        },
        dragleave: () => {
          setIsDragging(false);
          return false;
        },
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        const itemsArray = Array.from(items);
        const imageItem = itemsArray.find(item =>
          item.type.startsWith('image/'),
        );

        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = e => {
              const src = e.target?.result as string;
              editor?.chain().focus().setImage({ src }).run();
            };
            reader.readAsDataURL(file);
          }
          return true;
        }

        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

    const reader = new FileReader();
    reader.onload = e => {
      const src = e.target?.result as string;
      editor.chain().focus().setImage({ src }).run();
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUrlSubmit = () => {
    if (!editor || !imageUrl) return;

    editor.chain().focus().setImage({ src: imageUrl }).run();
    setImageUrl('');
    setImageUrlDialogOpen(false);
  };

  const handleLinkSubmit = () => {
    if (!editor || !linkUrl) return;

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (linkText) {
      // If there's custom link text, delete any selection and insert the link
      if (hasSelection) {
        editor.chain().focus().deleteSelection().run();
      }
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${linkUrl}">${linkText}</a> `)
        .run();
    } else if (hasSelection) {
      // If text is selected but no custom text, apply link to selection
      editor
        .chain()
        .focus()
        .setLink({ href: linkUrl })
        .insertContent(' ')
        .run();
    } else {
      // No selection and no custom text, insert URL as link text
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${linkUrl}">${linkUrl}</a> `)
        .run();
    }

    setLinkText('');
    setLinkUrl('');
    setLinkDialogOpen(false);
  };

  const handleOpenLinkDialog = () => {
    if (!editor) return;

    // Check if we're already in a link and unlink if so
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    // Get selected text to pre-fill the text field
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, '');

    if (selectedText) {
      setLinkText(selectedText);
    } else {
      setLinkText('');
    }

    setLinkUrl('');
    setLinkDialogOpen(true);
  };

  if (!editor) {
    return null;
  }

  const hasActionButtonInDocument =
    getActionButtonsFromEditor(editor).length > 0;

  const toolbarGroups = [
    {
      key: 'formatting',
      items: [
        hasFeature('bold') ? (
          <MenuButton
            key="bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <Bold />
          </MenuButton>
        ) : null,
        hasFeature('italic') ? (
          <MenuButton
            key="italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <Italic />
          </MenuButton>
        ) : null,
        hasFeature('underline') ? (
          <MenuButton
            key="underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title="Underline"
          >
            <UnderlineIcon />
          </MenuButton>
        ) : null,
        hasFeature('strikethrough') ? (
          <MenuButton
            key="strikethrough"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            title="Strikethrough"
          >
            <Strikethrough />
          </MenuButton>
        ) : null,
        hasFeature('clearFormatting') ? (
          <MenuButton
            key="clearFormatting"
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }
            title="Clear Formatting"
          >
            <RemoveFormatting />
          </MenuButton>
        ) : null,
      ],
    },
    {
      key: 'lists',
      items: [
        hasFeature('bulletList') ? (
          <MenuButton
            key="bulletList"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List />
          </MenuButton>
        ) : null,
        hasFeature('orderedList') ? (
          <MenuButton
            key="orderedList"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Ordered List"
          >
            <ListOrdered />
          </MenuButton>
        ) : null,
        hasFeature('blockquote') ? (
          <MenuButton
            key="blockquote"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <Quote />
          </MenuButton>
        ) : null,
      ],
    },
    {
      key: 'links-and-media',
      items: [
        hasFeature('link') ? (
          <MenuButton
            key="link"
            onClick={handleOpenLinkDialog}
            isActive={editor.isActive('link')}
            title={editor.isActive('link') ? 'Remove Link' : 'Add Link'}
          >
            <Link2 />
          </MenuButton>
        ) : null,
        hasFeature('image') ? (
          <MenuButton
            key="image"
            onClick={() => fileInputRef.current?.click()}
            isActive={editor.isActive('image')}
            title="Upload Image (or drag & drop)"
          >
            <ImagePlus />
          </MenuButton>
        ) : null,
        hasFeature('imageUrl') ? (
          <MenuButton
            key="imageUrl"
            onClick={() => setImageUrlDialogOpen(true)}
            isActive={editor.isActive('image')}
            title="Add Image from URL"
          >
            <LinkIcon />
          </MenuButton>
        ) : null,
        hasFeature('mergeKeys') && workerAttributes.length > 0 ? (
          <MenuButton
            key="workerAttributes"
            onClick={() => {
              // Insert "{" to trigger the suggestion dropdown
              editor.chain().focus().insertContent('{').run();
            }}
            title="Insert Worker Attribute"
          >
            <span className="font-mono text-sm">{'{}'}</span>
          </MenuButton>
        ) : null,
      ],
    },
    {
      key: 'buttons',
      items: [
        hasFeature('actionButton') ? (
          <MenuButton
            key="actionButton"
            onClick={() => {
              // Explicitly pass fullWidth so new buttons render full-width by default.
              editor
                .chain()
                .focus()
                .insertActionButton({
                  fullWidth: true,
                  value: resolvedActionButtonDefaults.label,
                  link: resolvedActionButtonDefaults.link,
                  backgroundColor: resolvedActionButtonDefaults.backgroundColor,
                })
                .run();
            }}
            disabled={hasActionButtonInDocument}
            title={
              hasActionButtonInDocument
                ? intl.formatMessage({
                    defaultMessage: 'Only one Action Button is allowed',
                    description:
                      'Toolbar tooltip: editor allows a single CTA per message',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Insert Action Button',
                    description:
                      'Toolbar: add a clickable CTA to pool/SMS or email content',
                  })
            }
            className="w-fit px-1 py-1.5"
          >
            <img src={InsertButtonIcon} alt="Button" />
            <FormattedMessage
              defaultMessage="Button"
              description="Visible text on the toolbar for the CTA insert control"
            />
          </MenuButton>
        ) : null,
      ],
    },
    {
      key: 'history',
      items: [
        hasFeature('undo') ? (
          <MenuButton
            key="undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo />
          </MenuButton>
        ) : null,
        hasFeature('redo') ? (
          <MenuButton
            key="redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo />
          </MenuButton>
        ) : null,
      ],
    },
  ]
    .map(group => ({
      ...group,
      items: group.items.filter(Boolean) as React.ReactNode[],
    }))
    .filter(group => group.items.length > 0);

  return (
    <div
      className={clsx(
        'relative flex flex-col overflow-hidden',
        height === 'full' && 'h-full min-h-0',
        !isBorderless && 'rounded-lg border',
      )}
    >
      {editable && (
        <div
          className={clsx(
            'flex shrink-0 flex-wrap items-center gap-0.5 p-2',
            !isBorderless && 'border-b bg-gray-50',
          )}
        >
          {toolbarGroups.map((group, idx) => (
            <Fragment key={group.key}>
              {idx > 0 && <ToolbarSeparator />}
              {group.items}
            </Fragment>
          ))}
        </div>
      )}

      <div
        className={clsx(
          'relative min-h-0 overflow-y-auto',
          height === 'fixed' && 'h-[320px]',
          height === 'full' && 'flex-1',
        )}
      >
        <EditorContent editor={editor} />

        {/* Drag and drop overlay */}
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-b-lg border-2 border-dashed border-blue-400 bg-blue-50/80">
            <div className="flex flex-col items-center gap-2 text-blue-600">
              <ImagePlus className="h-8 w-8" />
              <p className="text-sm font-medium">
                <FormattedMessage
                  defaultMessage="Drop image here"
                  description="Drag-and-drop overlay when adding an image to the editor"
                />
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Link URL Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Add Link"
                description="Modal title to insert a hyperlink in rich text"
              />
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 p-4">
            <Input
              label="Link Text"
              placeholder="Enter text to display"
              value={linkText}
              onChange={e => setLinkText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleLinkSubmit();
                }
              }}
              autoFocus
            />
            <Input
              label="URL"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleLinkSubmit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button onClick={handleLinkSubmit} disabled={!linkUrl}>
              <FormattedMessage
                defaultMessage="Add Link"
                description="Modal primary: confirm URL and link text in editor"
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image URL Dialog */}
      <Dialog open={imageUrlDialogOpen} onOpenChange={setImageUrlDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Add Image from URL"
                description="Modal to embed a remote image by pasting a URL"
              />
            </DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <Input
              label="Image URL"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleImageUrlSubmit();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button onClick={handleImageUrlSubmit} disabled={!imageUrl}>
              <FormattedMessage
                defaultMessage="Add Image"
                description="Confirm image URL and insert into message body"
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActionButtonEditDialog
        open={actionButtonDialogValues !== null}
        onOpenChange={open => {
          if (!open) {
            actionButtonUpdateRef.current = null;
            setActionButtonDialogValues(null);
          }
        }}
        defaultValues={actionButtonDialogValues ?? resolvedActionButtonDefaults}
        onSubmit={({ label, link, backgroundColor }) => {
          actionButtonUpdateRef.current?.({
            value: label,
            link,
            backgroundColor,
          });
        }}
      />
    </div>
  );
}

interface MenuButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  className?: string;
}

function MenuButton({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
  className,
}: MenuButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'h-8 w-8 p-0 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
        isActive && 'bg-gray-200',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {children}
    </Button>
  );
}

function ToolbarSeparator() {
  return <div className="mx-1 h-6 w-px bg-gray-300" />;
}
